import { Injectable } from '@angular/core';
import { IamService } from '@app/core/services/iam.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, forkJoin } from 'rxjs';
import { catchError, map, switchMap, concatMap, mergeMap, withLatestFrom, defaultIfEmpty } from 'rxjs/operators';
import { GroupsActions } from '../actions';
import * as fromRoot from '@app/reducers';
import * as fromAdmin from '@app/client/account/admin/reducers';
import { select, Store } from '@ngrx/store';
import { TokenService } from '@app/core/services/token.service';

const SCIMGroupName = 'SCIM';

@Injectable()
export class GroupsEffects {

  loadGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadGroups),
      switchMap(() => {
        return this.iamService.listGroups().pipe(
          map(res => {
            const { groups } = res;
            const groupsWithoutSCIMGroup = groups.filter(group => group.name !== SCIMGroupName);
            return GroupsActions.LoadGroupsSuccess({ groups: groupsWithoutSCIMGroup });
          }),
          catchError(error =>
            of(GroupsActions.LoadGroupsFailure({ error: error.message }))
          ));
      })
    )
  );

  loadGroupDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadGroupDetails),
      switchMap((action) => {
        return this.iamService.findGroup(action.id).pipe(
          map(res => {
            return GroupsActions.LoadGroupDetailsSuccess(res);
          }),
          catchError(error =>
            of(GroupsActions.LoadGroupDetailsFailure({ error: error.message }))
          ));
      })
    )
  );

  updateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.UpdateGroup),
      switchMap((action) => {
        return this.iamService.updateGroup(action.group.id, action.group).pipe(map(({ group }) => {
          return GroupsActions.UpdateGroupSuccess({ group });
        }),
          catchError(error =>
            of(GroupsActions.UpdateGroupFailure({ error: error.message }))
          ));
      })
    )
  );

  loadGroupRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadGroupRoles),
      switchMap((action) => {
        return forkJoin([this.iamService.listGroupRoles(action.id), this.iamService.listTeams()]).pipe(
          mergeMap(([resRoles, resTeams]: any[]) => {
            const { roles } = resRoles;
            const { teams } = resTeams;
            const parsedRoles = roles.map(role => {
              const team = teams.find(item => item.id === role.name.split('$')[0]);
              if (team) {
                const parsedRole = { ...role, roleDescription: role.description, teamName: team.name, teamId: team.id };
                return parsedRole;
              }
              return role;
            });
            return of(GroupsActions.LoadGroupRolesSuccess({ roles: parsedRoles }));
          }),
          catchError(error =>
            of(GroupsActions.LoadGroupRolesFailure({ error: error.message }))
          ));
      })
    )
  );

  loadGroupMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadGroupMembers),
      withLatestFrom(this.store$.pipe(select(fromRoot.getActiveIdentity))),
      switchMap(([action, identity]: any[]) => {
        return this.iamService.listGroupUsers(action.id).pipe(
          map(res => {
            const SCIMUserName = this.tokenService.getAccountFromToken(identity.token.token) + '$scim';
            const { users } = res;
            const usersWithoutSCIMUser = users.filter(user => user.username !== SCIMUserName);
            return GroupsActions.LoadGroupMembersSuccess({ members: usersWithoutSCIMUser });
          }),
          catchError(error =>
            of(GroupsActions.LoadGroupMembersFailure({ error: error.message }))
          ));
      })
    )
  );

  attachRoleToGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.AttachRoleToGroup),
      switchMap((action) => {
        const teamId = action.role.name.split('$')[0];
        const reqs = [];
        return this.iamService.listGroupRoles(action.groupId).pipe(concatMap(resGroupRoles => {
          const { roles } = resGroupRoles;
          const rolesWithSameTeamId = roles.filter(role => role.name.split('$')[0] === teamId);
          if (rolesWithSameTeamId.length === 0) {
            reqs.push(this.iamService.attachTeamToGroup(action.groupId, teamId));
          }
          reqs.push(this.iamService.attachRoleToGroup(action.groupId, action.role.name));
          return forkJoin(reqs).pipe(
            concatMap(() => {
              return [GroupsActions.AttachRoleToGroupSuccess()];
            }),
            catchError(error =>
              of(GroupsActions.AttachRoleToGroupFailure({ error: error.message }))
            ));
        }),
          catchError(error =>
            of(GroupsActions.AttachRoleToGroupFailure({ error: error.message }))
          ));
      })
    )
  );

  attachMemberToGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.AttachMemberToGroup),
      switchMap((action) => {
        return this.iamService.attachUserToGroup(action.groupId, action.userId).pipe(map(res => {
          return GroupsActions.AttachMemberToGroupSuccess();
        }),
          catchError(error =>
            of(GroupsActions.AttachMemberToGroupFailure({ error: error.message }))
          ));
      })
    )
  );


  detachMemberFromGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.DetachMemberFromGroup),
      switchMap((action) => {
        return this.iamService.detachUserFromGroup(action.groupId, action.userId).pipe(map(() => {
          return GroupsActions.DetachMemberFromGroupSuccess();
        }),
          catchError(error =>
            of(GroupsActions.DetachMemberFromGroupFailure({ error: error.message }))
          ));
      })
    )
  );

  detachRoleFromGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.DetachRoleFromGroup),
      switchMap((action) => {
        const teamId = action.role.name.split('$')[0];
        const reqs = [];
        return this.iamService.listGroupRoles(action.groupId).pipe(concatMap(resGroupRoles => {
          const { roles } = resGroupRoles;
          const rolesWithSameTeamId = roles.filter(role => role.name.split('$')[0] === teamId);
          if (rolesWithSameTeamId.length <= 1) {
            reqs.push(this.iamService.detachTeamFromGroup(action.groupId, teamId));
          }
          reqs.push(this.iamService.detachRoleFromGroup(action.groupId, action.role.name));
          return forkJoin(reqs).pipe(
            map(() => {
              return GroupsActions.DetachRoleFromGroupSuccess();
            }),
            catchError(error =>
              of(GroupsActions.DetachRoleFromGroupFailure({ error: error.message }))
            ));
        }),
          catchError(error =>
            of(GroupsActions.DetachRoleFromGroupFailure({ error: error.message }))
          ));
      }))
  );

  deleteGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.DeleteGroup),
      switchMap(({ groupId }) => {
        const reqsLinks = [
          this.iamService.listGroupUsers(groupId),
          this.iamService.listGroupRoles(groupId),
          this.iamService.listGroupTeams(groupId)
        ];
        return forkJoin(reqsLinks).pipe(
          defaultIfEmpty([]),
          concatMap(([{ users }, { roles }, { teams }]: any[]) => {
            const reqsDetachRessources = [];
            if (users.length > 0) {
              reqsDetachRessources.push(...users.map(user => this.iamService.detachUserFromGroup(groupId, user.id)));
            }
            if (roles.length > 0) {
              reqsDetachRessources.push(...roles.map(role => this.iamService.detachRoleFromGroup(groupId, role.name)));
            }
            if (teams.length > 0) {
              reqsDetachRessources.push(...teams.map(team => this.iamService.detachTeamFromGroup(groupId, team.id)));
            }
            if (reqsDetachRessources.length > 0) {
              return forkJoin(reqsDetachRessources).pipe(
                defaultIfEmpty([]),
                concatMap(() => {
                  return this.iamService.deleteGroup(groupId).pipe(concatMap(() => {
                    return of(GroupsActions.DeleteGroupSuccess());
                  }));
                }),
                catchError(error =>
                  of(GroupsActions.DeleteGroupFailure({ error: error.message }))
                ));
            }
            return this.iamService.deleteGroup(groupId).pipe(concatMap(() => {
              return of(GroupsActions.DeleteGroupSuccess());
            }),
              catchError(error =>
                of(GroupsActions.DeleteGroupFailure({ error: error.message }))
              ));
          }),
          catchError(error =>
            of(GroupsActions.DeleteGroupFailure({ error: error.message }))
          ));
      })));

  createGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.CreateGroup),
      switchMap((action) => {
        return this.iamService.createGroup({ name: action.name }).pipe(
          map(res => {
            return GroupsActions.CreateGroupSuccess(res);
          }),
          catchError(error =>
            of(GroupsActions.CreateGroupFailure({ error: error.message }))
          ));
      })
    )
  );

  constructor(
    private actions$: Actions,
    private iamService: IamService,
    private tokenService: TokenService,
    private store$: Store<fromRoot.State & fromAdmin.State>
  ) { }

}
